import React from "react";
import type { LucideIcon } from "lucide-react";
import {
  Activity,
  AlignLeft,
  Building2,
  CalendarPlus,
  CircleDollarSign,
  GitBranch,
  Globe,
  History,
  Layers,
  Lightbulb,
  MapPin,
  Milestone,
  Palette,
  Shapes,
  Tag,
  Type,
  User,
  UserCheck,
  Users,
} from "lucide-react";
import { ENTITY_TYPES, TASK_BUDGET_FIELDS } from "@/other/constants/OtherKeys";
import {
  getTaskBoardUsers,
  renderBlackout,
  renderBoardUsers,
  renderBudgetAmount,
  renderBudgetLabel,
  renderColorStrip,
  renderLabeledDate,
  renderLabeledText,
  renderResourceIcon,
  renderTags,
  renderText,
  renderTitle,
  resolveAssignedBoardUsers,
  resolveBudgetLabelValue,
  resolveContractAmountValue,
  resolveCreatorName,
  resolveEligibleBoardUsers,
  resolveTaskLocation,
  registerKanbanCardFieldCatalog,
  type KanbanCardFieldRenderContext,
} from "@/modern/constants/kanbanFieldUtils";

type EntityTypeValue = (typeof ENTITY_TYPES)[keyof typeof ENTITY_TYPES];

export type KanbanCardFieldGroup = "budget" | "scheduling" | "metadata" | "resource" | "audit";

export type { KanbanCardFieldRenderContext };

export type KanbanCardFieldMockConfig = {
  value: unknown;
};

export type KanbanCardFieldDefinition = {
  id: string;
  label: string;
  group: KanbanCardFieldGroup;
  icon: LucideIcon;
  defaultVisible?: boolean;
  alwaysVisible?: boolean;
  /** When false, always-visible chrome (e.g. color strip) is omitted from the card-fields editor. */
  showInSettings?: boolean;
  recordField?: string;
  mockConfig?: KanbanCardFieldMockConfig;
  resolveValue?: (ctx: KanbanCardFieldRenderContext) => unknown;
  renderer: (ctx: KanbanCardFieldRenderContext) => React.ReactNode;
};

export const MAX_KANBAN_VISIBLE_FIELDS = 4;

export const KANBAN_CARD_FIELD_GROUP_LABELS: Record<KanbanCardFieldGroup, string> = {
  budget: "Budget",
  scheduling: "Scheduling",
  metadata: "Metadata",
  resource: "Resource",
  audit: "Audit",
};

const alwaysVisibleChrome = (icon: LucideIcon, renderer: KanbanCardFieldDefinition["renderer"]): Omit<KanbanCardFieldDefinition, "id" | "label" | "group"> => ({
  icon,
  alwaysVisible: true,
  renderer,
});

const field = (
  def: Omit<KanbanCardFieldDefinition, "icon" | "renderer"> & {
    icon: LucideIcon;
    renderer: KanbanCardFieldDefinition["renderer"];
  }
): KanbanCardFieldDefinition => def;

const PREVIEW_COLOR = "#1e88e5";

const PREVIEW_BOARD_USERS = [
  { id: "preview-1", name: "Alex Kim", profileImageURL: "" },
  { id: "preview-2", name: "Jordan Lee", profileImageURL: "" },
];

const PREVIEW_CREATED_AT = new Date("2026-01-15");
const PREVIEW_UPDATED_AT = new Date("2026-06-20");

const BUDGET_CONTRACT_AMOUNT_PATH = `budgetRecord.${TASK_BUDGET_FIELDS.contractAmount}`;
const BUDGET_TOTAL_COST_ESTIMATED_PATH = `budgetRecord.${TASK_BUDGET_FIELDS.totalCostEstimated}`;

const auditFields = (): KanbanCardFieldDefinition[] => [
  field({
    id: "createdBy",
    label: "Created By",
    group: "audit",
    icon: User,
    mockConfig: { value: "Alex Kim" },
    resolveValue: resolveCreatorName,
    renderer: renderLabeledText(),
  }),
  field({
    id: "createdAt",
    label: "Created At",
    group: "audit",
    icon: CalendarPlus,
    mockConfig: { value: PREVIEW_CREATED_AT },
    renderer: renderLabeledDate,
  }),
  field({
    id: "updatedAt",
    label: "Last Updated At",
    group: "audit",
    icon: History,
    mockConfig: { value: PREVIEW_UPDATED_AT },
    renderer: renderLabeledDate,
  }),
];

const TASK_CATALOG: KanbanCardFieldDefinition[] = [
  field({
    id: "color",
    label: "Color strip",
    group: "metadata",
    mockConfig: { value: PREVIEW_COLOR },
    ...alwaysVisibleChrome(Palette, renderColorStrip),
    showInSettings: false,
  }),
  field({
    id: "name",
    label: "Name",
    group: "metadata",
    mockConfig: { value: "FC - 1 Building Roof Fixes" },
    ...alwaysVisibleChrome(Type, renderTitle),
    showInSettings: true,
  }),
  field({
    id: "agendaTitle",
    label: "External Name",
    group: "metadata",
    icon: Type,
    mockConfig: { value: "Building Roof Fixes" },
    renderer: renderLabeledText(),
  }),
  field({
    id: "locationValue",
    label: "Location",
    group: "metadata",
    icon: MapPin,
    mockConfig: { value: "1420 Harbor Blvd, Austin, TX" },
    resolveValue: resolveTaskLocation,
    renderer: renderLabeledText("line-clamp-1"),
  }),
  field({
    id: "description",
    label: "Description",
    group: "metadata",
    icon: AlignLeft,
    defaultVisible: true,
    mockConfig: { value: "Review scope and finalize the proposal before client sign-off." },
    renderer: renderText(),
  }),
  field({
    id: "budgetLabel",
    label: "Budget label",
    group: "budget",
    icon: CircleDollarSign,
    recordField: BUDGET_TOTAL_COST_ESTIMATED_PATH,
    mockConfig: { value: 98_000 },
    resolveValue: resolveBudgetLabelValue,
    renderer: renderBudgetLabel,
  }),
  field({
    id: "contractAmount",
    label: "Contract amount",
    group: "budget",
    icon: CircleDollarSign,
    recordField: BUDGET_CONTRACT_AMOUNT_PATH,
    mockConfig: { value: 125_000 },
    resolveValue: resolveContractAmountValue,
    renderer: renderBudgetAmount,
  }),
  field({
    id: "statusValue",
    label: "Status",
    group: "metadata",
    icon: Activity,
    defaultVisible: true,
    mockConfig: { value: "In progress" },
    renderer: renderTags,
  }),
  field({
    id: "categoryValue",
    label: "Category",
    group: "metadata",
    icon: Layers,
    mockConfig: { value: "Commercial" },
    renderer: renderTags,
  }),
  field({
    id: "phaseValue",
    label: "Phase",
    group: "metadata",
    icon: Milestone,
    mockConfig: { value: "Planning" },
    renderer: renderTags,
  }),
  field({
    id: "teamValue",
    label: "Team",
    group: "metadata",
    icon: Users,
    mockConfig: { value: "Field crew" },
    renderer: renderTags,
  }),
  field({
    id: "tagsValue",
    label: "Tags",
    group: "metadata",
    icon: Tag,
    defaultVisible: true,
    mockConfig: { value: ["Exterior", "Roofing"] },
    renderer: renderTags,
  }),
  field({
    id: "clientName",
    label: "Client",
    group: "metadata",
    icon: Building2,
    mockConfig: { value: "Riverside Properties" },
    renderer: renderTags,
  }),
  field({
    id: "assignees",
    label: "Assigned users",
    group: "resource",
    icon: Users,
    defaultVisible: true,
    mockConfig: { value: PREVIEW_BOARD_USERS },
    resolveValue: getTaskBoardUsers,
    renderer: renderBoardUsers,
  }),
  ...auditFields(),
];

const RESOURCE_CATALOG: KanbanCardFieldDefinition[] = [
  field({
    id: "name",
    label: "Name",
    group: "metadata",
    mockConfig: { value: "Alex Morgan" },
    ...alwaysVisibleChrome(Type, renderTitle),
    showInSettings: true,
  }),
  field({
    id: "location",
    label: "Location",
    group: "metadata",
    icon: MapPin,
    mockConfig: { value: "Austin, TX" },
    renderer: renderLabeledText("line-clamp-1"),
  }),
  field({
    id: "description",
    label: "Description",
    group: "metadata",
    icon: AlignLeft,
    defaultVisible: true,
    mockConfig: { value: "Senior project manager with roofing and siding expertise." },
    renderer: renderText(),
  }),
  field({
    id: "statusValue",
    label: "Status",
    group: "metadata",
    icon: Activity,
    defaultVisible: true,
    mockConfig: { value: "Active" },
    renderer: renderTags,
  }),
  field({
    id: "categoryValue",
    label: "Category",
    group: "metadata",
    icon: Layers,
    mockConfig: { value: "Operations" },
    renderer: renderTags,
  }),
  field({
    id: "teamValue",
    label: "Team",
    group: "metadata",
    icon: Users,
    mockConfig: { value: "Install" },
    renderer: renderTags,
  }),
  field({
    id: "iconValue",
    label: "Icon",
    group: "resource",
    icon: Shapes,
    mockConfig: { value: "user-hard-hat" },
    renderer: renderResourceIcon,
  }),
  field({
    id: "typeValue",
    label: "Type",
    group: "resource",
    icon: Layers,
    defaultVisible: true,
    mockConfig: { value: "Employee" },
    renderer: renderTags,
  }),
  field({
    id: "jobTitle",
    label: "Sub Type",
    group: "resource",
    icon: GitBranch,
    mockConfig: { value: "Project Manager" },
    renderer: renderLabeledText("line-clamp-1"),
  }),
  field({
    id: "tagsValue",
    label: "Tags",
    group: "metadata",
    icon: Tag,
    mockConfig: { value: ["Certified", "Lead"] },
    renderer: renderTags,
  }),
  field({
    id: "assignedTo",
    label: "Assigned To",
    group: "resource",
    icon: Users,
    defaultVisible: true,
    mockConfig: { value: PREVIEW_BOARD_USERS },
    resolveValue: resolveAssignedBoardUsers,
    renderer: renderBoardUsers,
  }),
  field({
    id: "eligibleUsers",
    label: "Points Eligible",
    group: "resource",
    icon: UserCheck,
    mockConfig: { value: PREVIEW_BOARD_USERS },
    resolveValue: resolveEligibleBoardUsers,
    renderer: renderBoardUsers,
  }),
  field({
    id: "hideAgenda",
    label: "Blackout",
    group: "resource",
    icon: Lightbulb,
    mockConfig: { value: true },
    renderer: renderBlackout,
  }),
  ...auditFields(),
];

const CLIENT_CATALOG: KanbanCardFieldDefinition[] = [
  field({
    id: "name",
    label: "Name",
    group: "metadata",
    mockConfig: { value: "Riverside Properties" },
    ...alwaysVisibleChrome(Type, renderTitle),
    showInSettings: true,
  }),
  field({
    id: "website",
    label: "Website",
    group: "metadata",
    icon: Globe,
    defaultVisible: true,
    mockConfig: { value: "riversideproperties.com" },
    renderer: renderText("line-clamp-1 truncate"),
  }),
  field({
    id: "address",
    label: "Address",
    group: "metadata",
    icon: MapPin,
    mockConfig: { value: "1420 Harbor Blvd, Austin, TX" },
    renderer: renderText("line-clamp-1"),
  }),
  field({
    id: "statusValue",
    label: "Status",
    group: "metadata",
    icon: Activity,
    defaultVisible: true,
    mockConfig: { value: "Active" },
    renderer: renderTags,
  }),
  field({
    id: "categoryValue",
    label: "Category",
    group: "metadata",
    icon: Layers,
    defaultVisible: true,
    mockConfig: { value: "Residential" },
    renderer: renderTags,
  }),
  field({
    id: "teamValue",
    label: "Team",
    group: "metadata",
    icon: Users,
    defaultVisible: true,
    mockConfig: { value: "Sales" },
    renderer: renderTags,
  }),
  field({
    id: "tagsValue",
    label: "Tags",
    group: "metadata",
    icon: Tag,
    mockConfig: { value: ["VIP", "Referral"] },
    renderer: renderTags,
  }),
  ...auditFields(),
];

export const KANBAN_CARD_FIELD_CATALOG: Partial<Record<EntityTypeValue, KanbanCardFieldDefinition[]>> = {
  [ENTITY_TYPES.TASK]: TASK_CATALOG,
  [ENTITY_TYPES.RESOURCE]: RESOURCE_CATALOG,
  [ENTITY_TYPES.CLIENT]: CLIENT_CATALOG,
};

registerKanbanCardFieldCatalog(KANBAN_CARD_FIELD_CATALOG, MAX_KANBAN_VISIBLE_FIELDS);
